import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

export type SshConnState = 'connecting' | 'open' | 'closed' | 'error';

interface SshTerminalViewProps {
  /** Whether the terminal should connect. */
  active: boolean;
  /** Public IPv4 of the reserved machine to SSH into (super-admin host path). */
  host?: string;
  /** Debug-bot id (operator path); the backend resolves the host server-side. */
  botId?: string;
  /** Notified whenever the WebSocket connection state changes. */
  onStateChange?: (state: SshConnState) => void;
  /** Height of the terminal surface. */
  height?: string | number;
}

// The WS base is derived from the same origin the REST API uses so the terminal
// follows the deployed backend (nginx upgrades /ws/* to a websocket in prod).
const wsBaseUrl = (): string => {
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  return apiBase.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
};

/**
 * Reusable in-browser SSH terminal surface (xterm.js) bridged over the backend
 * `/ws/ssh` WebSocket. Connects to either a reservation `host` (super-admin) or a
 * debug `botId` (operator). Renders only the terminal Box — wrap it in your own
 * dialog/panel chrome. Auth token + tenant are taken from sessionStorage, matching
 * the REST client.
 */
const SshTerminalView: React.FC<SshTerminalViewProps> = ({
  active,
  host,
  botId,
  onStateChange,
  height = '65vh',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [, setState] = useState<SshConnState>('connecting');

  const updateState = useCallback(
    (next: SshConnState) => {
      setState(next);
      onStateChange?.(next);
    },
    [onStateChange]
  );

  const doFit = useCallback(() => {
    const fit = fitRef.current;
    const term = termRef.current;
    const ws = wsRef.current;
    if (!fit || !term) return;
    try {
      fit.fit();
    } catch {
      return;
    }
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
    }
  }, []);

  useEffect(() => {
    if (!active || (!host && !botId) || !containerRef.current) return undefined;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: '"Space Mono", "Menlo", monospace',
      fontSize: 13,
      theme: { background: '#0b0f14' },
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    termRef.current = term;
    fitRef.current = fit;
    // Fit once the container transition settles so measurements are correct.
    const fitTimer = window.setTimeout(() => {
      try {
        fit.fit();
      } catch {
        /* container not measurable yet */
      }
    }, 50);

    const token = sessionStorage.getItem('authToken') || '';
    const tenantId = sessionStorage.getItem('selectedTenant') || '';
    const params = new URLSearchParams({
      token,
      cols: String(term.cols || 80),
      rows: String(term.rows || 24),
    });
    if (botId) params.set('bot_id', botId);
    else if (host) params.set('host', host);
    if (tenantId) params.set('tenant_id', tenantId);

    const ws = new WebSocket(`${wsBaseUrl()}/ws/ssh?${params.toString()}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    updateState('connecting');

    ws.onopen = () => {
      updateState('open');
      doFit();
      term.focus();
    };
    ws.onmessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        term.write(event.data);
      } else {
        term.write(new Uint8Array(event.data as ArrayBuffer));
      }
    };
    ws.onerror = () => updateState('error');
    ws.onclose = (event: CloseEvent) => {
      updateState('closed');
      const reason = event.reason ? `: ${event.reason}` : '';
      term.write(`\r\n\x1b[33m[connection closed${reason}]\x1b[0m\r\n`);
    };

    const onData = term.onData((data: string) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'input', data }));
      }
    });
    const onResize = term.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols, rows }));
      }
    });
    const onWindowResize = () => doFit();
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.clearTimeout(fitTimer);
      window.removeEventListener('resize', onWindowResize);
      onData.dispose();
      onResize.dispose();
      // Detach handlers first so a late close/message event can't write to the
      // terminal we are about to dispose.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
      term.dispose();
      wsRef.current = null;
      termRef.current = null;
      fitRef.current = null;
    };
  }, [active, host, botId, doFit, updateState]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height,
        width: '100%',
        backgroundColor: '#0b0f14',
        borderRadius: 1,
        p: 1,
        overflow: 'hidden',
      }}
    />
  );
};

export default SshTerminalView;
